'use client';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { icon } from '@/uiPrimitives/media/icon';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { Collapse } from '@/uiComposites/interactive/Collapse';
import { Fragment, useId, useState } from 'react';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { box } from '@/uiPrimitives/layout/box';
import { stack } from '@/uiPrimitives/layout/stack';
import { Translation } from '@/globals/translate/translations';
import { locationOpeningTimesGrid } from './ContactBlockLocation.css';
import { warningIcon } from '@/uiIcons/warningIcon.css';
import { delayIconTransitionHover } from '@/uiComposites/interactive/accordion/Accordion.css';
import { locationCircleIcon } from '@/uiIcons/locationCircleIcon.css';
import { languageCode } from '@/globals/config/localization.config';

export type Location =
	| {
			title: string;
			address: string;
			zipcode: string;
			city: string;
			openingHoursMessageNL?: string; // only available in Dutch :(
			openingHours: Record<string, Array<string>>;
	  }
	| undefined;

type Labels = {
	closed: Translation<'Closed'>;
	openingHours: Translation<'Opening hours'>;
	monday: Translation<'Monday'>;
	tuesday: Translation<'Tuesday'>;
	wednesday: Translation<'Wednesday'>;
	thursday: Translation<'Thursday'>;
	friday: Translation<'Friday'>;
	saturday: Translation<'Saturday'>;
	sunday: Translation<'Sunday'>;
};

export const ContactBlockLocation = ({
	location,
	labels,
}: {
	location: Location;
	labels: Labels;
}) => {
	const [isOpened, setIsOpened] = useState(false);
	const idSuffix = useId();

	if (location == null) return null;

	const { title, address, zipcode, city, openingHours, openingHoursMessageNL } =
		location;
	const id = `location-${title}-${idSuffix}`;

	return (
		<div>
			{' '}
			<button
				onClick={() => setIsOpened(!isOpened)}
				aria-controls={id}
				aria-expanded={isOpened}
				className={classes(
					shelf({
						gap: '2 | h2',
						alignItems: 'start',
					}),
				)}
			>
				<span
					className={occupy({
						blockSize: '-1 | small',
					})}
				>
					<i
						aria-hidden
						className={classes(
							icon({
								icon: locationCircleIcon,
								display: 'inline-block',
								blockSize: 'auto',
								inlineSize: '4 | betweenListItems',
								fill: 'positiveBlue',
							}),
						)}
					/>
				</span>
				<span
					className={classes(
						shelf({
							gap: '-1 | small',
							gridTemplateColumns: 'minmax(0, 1fr) auto',
							alignItems: 'baseline',
						}),
						textStyle({
							color: 'positiveBlue',
							fontWeight: isOpened ? 'bold' : undefined,
						}),
					)}
				>
					<h3 className={classes(textBlock({}))}>{title}</h3>
					<i
						className={classes(
							delayIconTransitionHover,
							icon({
								icon: chevronIcon,
								adjustIcon: 'rotate90deg',
								flip: isOpened ? 'horizontal:hover' : 'horizontalNone:hover',
								blockSize: '1ex',
							}),
						)}
					/>
				</span>
			</button>
			<Collapse id={id} isOpened={isOpened}>
				<div
					className={classes(
						box({
							paddingBlockStart: '3 | h1',
							paddingBlockEnd: '0 | p',
							paddingInlineStart: '7 | betweenH3Sections',
						}),
					)}
				>
					<div className={classes(stack({ rowGap: '3 | h1' }))}>
						<div className={classes(stack({ rowGap: '1 | h3' }))}>
							{address && <p className={classes(textBlock({}))}>{address}</p>}
							{city && zipcode && (
								<p className={classes(textBlock({}))}>{`${zipcode} ${city}`}</p>
							)}
						</div>
						<h4 className={classes(textBlock({ fontWeight: 'bold' }))}>
							{labels.openingHours}
						</h4>
						<div
							className={classes(
								locationOpeningTimesGrid,
								textStyle({ capSize: '-1 | small' }),
							)}
						>
							{Object.entries(openingHours).map(([key, hoursArray]) => (
								<Fragment key={`${title}-${key}`}>
									<span className={classes(textBlock({}))}>
										{labels[key as keyof typeof labels]}
									</span>
									<div className={classes(stack({ rowGap: '0 | p' }))}>
										{hoursArray.length === 0 && (
											<span
												className={classes(
													textBlock({ color: 'positiveMuted' }),
												)}
											>
												{labels.closed}
											</span>
										)}
										{hoursArray.length > 0 &&
											hoursArray.map((hours, i) => (
												<span
													key={`${key}-${i}`}
													className={classes(textBlock({}))}
												>
													{hours}
												</span>
											))}
									</div>
								</Fragment>
							))}
						</div>
						{languageCode === 'nl' && openingHoursMessageNL && (
							<div className={classes(shelf({ columnGap: '-1 | small' }))}>
								<i
									className={classes(
										icon({ icon: warningIcon, fill: 'positiveMuted' }),
									)}
								/>
								<p
									className={classes(
										textBlock({
											color: 'positiveMuted',
											capSize: '-1 | small',
										}),
									)}
								>
									{openingHoursMessageNL}
								</p>
							</div>
						)}
					</div>
				</div>
			</Collapse>
		</div>
	);
};
